import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, MessageSquare, Check } from "lucide-react";
import { toast } from "sonner";

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const lastTopId = useRef(null);
  const navigate = useNavigate();

  const load = async (announceNew = true) => {
    try {
      const { data } = await api.get("/notifications");
      const newest = data.items[0]?.id;
      if (announceNew && lastTopId.current && newest && newest !== lastTopId.current) {
        const newOnes = data.items.findIndex((n) => n.id === lastTopId.current);
        const count = newOnes === -1 ? data.items.length : newOnes;
        if (count > 0) {
          toast.message(`${count} new notification${count > 1 ? "s" : ""}`, {
            description: data.items[0].title,
          });
        }
      }
      lastTopId.current = newest || lastTopId.current;
      setItems(data.items);
      setUnread(data.unread);
    } catch {}
  };

  useEffect(() => {
    load(false);
    const t = setInterval(() => load(true), 15000);
    return () => clearInterval(t);
  }, []);

  // close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (!e.target.closest("[data-notif-root]")) setOpen(false);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [open]);

  const openItem = async (n) => {
    if (!n.read) {
      await api.patch(`/notifications/${n.id}/read`);
    }
    setOpen(false);
    navigate(n.ref_url);
    load(false);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load(false);
  };

  return (
    <div data-notif-root className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative p-2 hover:bg-zinc-100 border border-zinc-200"
        data-testid="notification-bell"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 bg-red-600 text-white text-[10px] font-bold flex items-center justify-center rounded-full">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white border border-zinc-300 shadow-lg z-50" data-testid="notification-panel">
          <div className="border-b border-zinc-200 px-4 py-3 flex items-center justify-between">
            <span className="font-medium text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} className="text-[10px] uppercase tracking-wider text-[#002FA7] hover:underline flex items-center gap-1">
                <Check size={11} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-12 text-sm text-zinc-500 text-center">No notifications yet.</div>
            )}
            {items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left px-4 py-3 border-b border-zinc-200 hover:bg-zinc-50 flex gap-3 ${!n.read ? "bg-blue-50/40" : ""}`}
                data-testid={`notif-item-${n.id}`}
              >
                <div className={`w-7 h-7 flex items-center justify-center shrink-0 ${!n.read ? "bg-[#002FA7] text-white" : "bg-zinc-100 text-zinc-600"}`}>
                  <MessageSquare size={12} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{n.title}</div>
                  {n.task_title && (
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 truncate">on “{n.task_title}”</div>
                  )}
                  <div className="text-xs text-zinc-600 line-clamp-2 mt-0.5">{n.body}</div>
                  <div className="text-[10px] font-mono text-zinc-400 mt-1 uppercase tracking-wider">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </div>
                {!n.read && <div className="w-1.5 h-1.5 bg-[#002FA7] rounded-full mt-2 shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
